/**
 * `<AutoConfiguredLabel>` — small green inline label rendered next to
 * Advanced Settings fields whose value was populated automatically by
 * Extended Discovery (template fingerprint match) or the ONVIF probe
 * rather than typed by the user (Multi-Add Phase 2 — auto-configured field labeling).
 *
 * The component owns two responsibilities:
 *
 *   1. Render gate — return `null` when the depot slice does NOT carry
 *      `(deviceId, fieldPath)`; return the green `<span>` when it does.
 *      The render gate uses `getAutoConfiguredValue` (NOT `isAutoConfigured`)
 *      so the comparison value is fetched in a single selector read.
 *
 *   2. Self-eviction on user-edit — subscribe to the live input `value`
 *      via the consumer-supplied prop and, when it diverges from the
 *      recorded `autoValue`, dispatch `removeAutoConfiguredField` so the
 *      label disappears on the next render. This is the design's core
 *      decision: per-field user-edit detection lives INSIDE the label
 *      via value comparison; no separate dirty-tracking primitive is
 *      introduced.
 *
 * The slice (`device/store/auto-configured-fields.slice.ts`) is the
 * source-of-truth; this component is a thin React veneer over its read
 * and `remove` accessors.
 *
 * Per `ui-architecture` (ai.knowledge/global): the green colour resolves
 * via the design-system `text-success` Tailwind theme token so Iris and
 * the Hive whitelabel variant resolve the same semantic colour to their
 * own brand-specific success swatch. Never `text-green-400` or similar
 * literal — that would freeze the colour to one variant.
 *
 * Per `think-in-systems` (ai.knowledge/global): the eviction logic lives
 * in the consumer of the slice (this component) via simple value
 * comparison. We deliberately do NOT introduce a generic dirty-tracking
 * system; per-field eviction by value-comparison is cheap, framework-
 * agnostic, and avoids cross-slice coupling.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). Related rules
 * referenced in the inline implementation comments below:
 *   - `code-comments-required` (one-liner, intent-only)
 *   - `stay-in-scope` (the label is a thin veneer — no form rewrites)
 *
 * Why a class concat + optional `twMerge` rather than always-twMerge:
 * `twMerge` collapses conflicting Tailwind classes — desirable when the
 * consumer passes overrides like a different `text-*` size. We invoke
 * it only when a className is supplied so the common no-override path
 * stays allocation-free.
 */
import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { Device } from '@studio/models/device';
import { useDepot } from '#~/depot/hooks.js';
import {
  getAutoConfiguredValue,
  removeAutoConfiguredField
} from '#~/device/store/auto-configured-fields.slice.js';

/**
 * Props for `<AutoConfiguredLabel>`.
 *
 * `value` is the LIVE input value owned by the parent (the form
 * primitive driving the field). The component compares it against the
 * slice's recorded snapshot to detect user-edits — a divergence
 * (both defined, different) triggers eviction.
 */
export interface AutoConfiguredLabelProps {
  /** Device whose auto-config slice bucket this label addresses. */
  deviceId: Device['id'];
  /**
   * Dotted field path used as the bucket key (e.g. `manufacturer.brand`,
   * `video.host`). Must match the path the recording call sites in
   * the multi-add and single-add paths used when populating the slice.
   */
  fieldPath: string;
  /**
   * Live value of the input the label decorates. When this diverges
   * from the recorded auto-fill value, the label self-evicts.
   */
  value: string | undefined;
  /** Optional class override merged via `twMerge`. */
  className?: string;
}

/**
 * `<AutoConfiguredLabel>` — see file header for full rationale.
 */
export const AutoConfiguredLabel = ({
  deviceId,
  fieldPath,
  value,
  className
}: AutoConfiguredLabelProps): ReactElement | null => {
  // Subscribe to the slice via the depot selector. `useDepot` re-renders
  // the label only when this specific `(deviceId, fieldPath)` entry
  // changes — adjacent entries on the same device don't cause noise.
  const autoValue = useDepot((state) => getAutoConfiguredValue(state, deviceId, fieldPath));

  // Producer reference is stable across renders — the depot exposes it
  // once at construction. Pulling it here keeps the eviction effect
  // dependency list minimal.
  const produce = useDepot((state) => state.produce);

  // User-edit eviction. Fires only when both values are defined AND
  // differ — guards against:
  //   - `autoValue === undefined`: the field was never auto-configured,
  //     or has already been evicted. Nothing to do.
  //   - `value === undefined`: the consumer's input is uncontrolled or
  //     transiently unset. Treating that as an "edit" would evict on
  //     mount before the user touched anything.
  //   - `value === autoValue`: a programmatic re-set (e.g. a re-probe
  //     wrote the same value back). The label should remain.
  useEffect(() => {
    if (autoValue !== undefined && value !== undefined && value !== autoValue) {
      produce((draft) => {
        removeAutoConfiguredField(draft, deviceId, fieldPath);
      });
    }
  }, [autoValue, value, produce, deviceId, fieldPath]);

  // Render gate — no slice entry, no label. Layout collapses cleanly
  // (parent does not see an empty span).
  if (autoValue === undefined) return null;

  // Per `ui-architecture` (ai.knowledge/global): `text-success` resolves
  // via the design-system theme so Iris and Hive each get their own
  // brand-specific success swatch from one semantic token.
  //
  // `text-[10px]` pins the size below `text-xs` (12px) — the spec's
  // visual reference shows a deliberately small inline tag. If the
  // design system later ships a `text-2xs` / `text-9xs` token, that
  // arbitrary-value bracket can be replaced.
  //
  // `select-none` prevents the label from accidentally being included
  // in a user's text selection of the form field, which would be
  // confusing copy-paste behaviour.
  //
  // `font-medium` matches the design reference's slightly heavier
  // weight that distinguishes the tag from regular field-label copy.
  const baseClass = 'select-none text-[10px] font-medium text-success';
  const finalClass = className ? twMerge(baseClass, className) : baseClass;

  return (
    <span
      aria-label='Auto Configured'
      className={finalClass}
      data-testid={`auto-configured-label-${fieldPath}`}
    >
      Auto Configured
    </span>
  );
};

AutoConfiguredLabel.displayName = 'AutoConfiguredLabel';
